import { InputAdornment } from '@mui/material';
import { ConversationStatus } from '@provision/conversation-types';
import { UrlParams } from '@provision/portal-routes';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormAutocomplete } from 'src/components/Forms/FormAutocomplete/form-autocomplete';
import { AutoCompleteOption } from 'src/components/Forms/types';
import { filterAdornmentSx } from 'src/components/ModalContainer/modal-list.styles';
import { Conversation } from 'src/graphql';
import { useSearchConversations } from 'src/graphql/entities/search/search-conversation';
import { useSelectedConversation } from 'src/pages/Conversations';
import { useScrollPaginateHandler } from 'src/utils/hooks';
import { useRouteParams } from 'src/utils/routing';
import {
  ContactSupportFormField,
  ContactSupportFormValues,
} from './contact-support-form.types';

interface SearchConversationFieldProps {
  required: boolean | string;
}
// LinkConversationData is lightweight type that includes minimal required fields for this component.
// It is needed to avoid using Conversation type in conjunction with `useForm` as it fails to properly
// resolve types and TypeScript complains on "excessively deep and possibly infinite" type instantiation.
export type LinkConversationData = Pick<
  Conversation,
  'friendlyId' | 'subject' | 'conversationId' | 'partnerId'
>;

export type LinkConversationOption = AutoCompleteOption & LinkConversationData;

const toLinkConversationOption = ({
  friendlyId,
  subject,
  conversationId,
  partnerId,
}: LinkConversationData): LinkConversationOption => ({
  id: conversationId,
  label: `${friendlyId} | ${subject}`,
  friendlyId,
  subject,
  conversationId,
  partnerId,
});

export const SearchConversationField = ({ required }: SearchConversationFieldProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { setValue } = useFormContext<ContactSupportFormValues>();

  const { recordId: conversationId } = useRouteParams(UrlParams.recordId);

  const { selectedConversation } = useSelectedConversation({
    conversationId: conversationId || '',
  });

  useEffect(() => {
    if (selectedConversation) {
      setValue(
        ContactSupportFormField.LinkedConversation,
        toLinkConversationOption(selectedConversation),
      );
    }
  }, [setValue, selectedConversation]);

  const searchConversationsFilter = useMemo(
    () => [
      {
        attribute: 'conversationStatusId',
        operator: '!=',
        value: ConversationStatus.Closed,
      },
    ],
    [],
  );

  const { records, loadMore, isLoading, hasMore } = useSearchConversations({
    searchTerm,
    filter: searchConversationsFilter,
    sort: {
      attribute: 'subject',
    },
  });

  const options = useMemo(() => records.map(toLinkConversationOption), [records]);

  const handleScroll = useScrollPaginateHandler({
    loadMore,
    enabled: hasMore,
    thresholdPx: 200,
  });

  return (
    <FormAutocomplete<LinkConversationOption>
      type="autocomplete"
      name={ContactSupportFormField.LinkedConversation}
      label="Linked Conversation"
      options={options}
      loading={isLoading}
      required={required}
      showErrorText
      data-testid="contact-support-linked-conversation"
      textFieldProps={{ placeholder: 'Search & Select Conversation' }}
      inputProps={{
        startAdornment: (
          <InputAdornment position="start" sx={filterAdornmentSx}>
            <Search aria-hidden />
          </InputAdornment>
        ),
      }}
      autocompleteProps={{
        autoHighlight: true,
        // A search box, not a picker: no chevron.
        popupIcon: null,
        onInputChange: (_, value, reason) => {
          if (reason === 'input' || reason === 'clear') {
            setSearchTerm(value);
          }
        },
        slotProps: { listbox: { onScroll: handleScroll } },
      }}
    />
  );
};
